<template>
  <ProductsCartModal
    v-if="showProductsCartModal"
    :type="productCartType"
    :plants="plants"
    @closeModal="showProductsCartModal = false"
  />
  <CopyProductsModal
    v-if="copyModalVisible"
    :plants="plants"
    :productIds="selectedProductIds"
    @closeModal="onCopyModalClosed"
  />
  <TerminationModal
    v-if="terminationModalVisible"
    :plants="plants"
    :productIds="selectedProductIds"
    @closeModal="onTerminationModalClosed"
  />
  <DownloadsModal
    v-if="showDownloadsModal"
    :plants="plants"
    @closeModal="showDownloadsModal = false"
  />

  <div data-cy="products-mobile-view">
    <section class="search-section" data-cy="search-section">
      <div class="search-wrapper">
        <TableSearch
          class="product-mobile-search"
          :value="searchInput"
          placeholder="Enter Search"
          @searchSubmitted="onSearchSubmitted"
          data-cy="products-search"
        />
        <!-- Search by Name or Brand or Plant -->
        <span
          class="filter-icon"
          @click="showFilters = true"
          data-cy="filters-button"
        >
          <FilterIcon fill="black" />
        </span>
      </div>

      <div class="sort-wrapper">
        <DropDownList
          class="sort-dropdown"
          :textField="'text'"
          :data-items="sortOptions"
          v-model="selectedSortOption"
          @change="onSortChanged"
          data-cy="sort-dropdown"
        />
      </div>
    </section>

    <ProductFiltersMenu
      v-if="showFilters"
      :getPropValues="getPropValuesAsync"
      :filters="productFilters"
      @apply="onFiltersApplied"
      @clear="onFiltersCleared"
      @close="showFilters = false"
    />

    <section class="items-section">
      <header class="items-sort">
        <div class="action-island">
          <div v-if="canOpenOrModifyCarts" class="action-island__carts">
            <Button
              v-for="action in cartActions"
              :key="action.subApplication"
              :class="action.class"
              type="button"
              :disabled="action.count === 0"
              @click="action.onClick"
            >
              <AddCartIcon v-if="action.icon == 'add'" />
              <DeleteCartIcon v-if="action.icon == 'delete'" />
              {{ action.count }}
            </Button>
          </div>

          <Button
            class="download"
            type="button"
            :loading="!viewLoaded"
            @click="showDownloadsModal = true"
          >
            <DownloadIcon style="fill: white" />
          </Button>
        </div>
      </header>

      <div v-if="canOpenOrModifyCarts" class="button-island">
        <Button
          type="button"
          theme-color="primary"
          class="copy-products"
          :disabled="!selectedProductIds.length"
          @click="onCopyClicked"
          data-cy="add-ingredients-button"
          >Copy Selected Products</Button
        >
        <Button
          type="button"
          theme-color="error"
          class="terminate-products"
          :disabled="!selectedProductIds.length"
          @click="onTerminateClicked"
          data-cy="add-ingredients-button"
          >Terminate Selected Products</Button
        >
      </div>
    </section>

    <ProductList
      :totalProducts="totalProducts"
      :products="products"
      :defaultTake="productsTake"
      :skip="productsSkip"
      :loading="productsLoading"
      :canModifyCarts="canOpenOrModifyCarts"
      @productsChanged="onProductsChanged"
    />

    <section v-if="canOpenOrModifyCarts" class="new-product">
      <div class="trigger" @click="viewNew = true" v-if="!viewNew">+</div>
      <ProductApplicationModal
        v-if="viewNew"
        :plants="plants"
        @closeModal="viewNew = false"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { EnumDpm } from "@/Domain/EnumDpm";
import ProductList from "./ProductList.vue";
import FilterIcon from "@/icons/FilterIcon.vue";
import AddCartIcon from "@/icons/AddCartIcon.vue";
import DownloadsModal from "./DownloadsModal.vue";
import { computed, onBeforeMount, ref } from "vue";
import DownloadIcon from "@/icons/DownloadIcon.vue";
import { useMainStore } from "@/stores/useMainStore";
import { Button } from "@progress/kendo-vue-buttons";
import TerminationModal from "./TerminationModal.vue";
import ProductsCartModal from "./ProductsCartModal.vue";
import CopyProductsModal from "./CopyProductsModal.vue";
import DeleteCartIcon from "@/icons/DeleteCartIcon.vue";
import ProductFiltersMenu from "./ProductFiltersMenu.vue";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { convertNumber } from "@/Domain/EnumGroupConverter";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";
import { useScheduleBStore } from "@/stores/useScheduleBStore";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { getLabelTypeString } from "@/shared/helpers/enumHelper";
import ProductApplicationModal from "./ProductApplicationModal.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import {
  boolYNConvert,
  FilterValuesTake,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import type { IdAndName } from "@/Domain/IdAndName";
import type { FilterField } from "@/Domain/FilterField";
import type { FilterGroup } from "@/Domain/FilterGroup";
import type { EnumLabelType } from "@/Domain/EnumLabelType";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import type { FilterExpression } from "@/Domain/FilterExpression";
import type { ProductWithSelection } from "@/api/kosherCertificates";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import type {
  GetProductDisplayScheduleBWithFilteringRequest,
  PlantWithSelection,
} from "@/api/scheduleB";
import { ProductAppGroup } from "@/shared/access/RoleGroups";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";

const main = useMainStore();
const scheduleStore = useScheduleBStore();

const totalProducts = ref<number>(0);
const products = ref<ProductWithSelection[]>([]);
const productsLoading = ref<boolean>(false);
const productsTake = ref<number>(100);
const productsSkip = ref<number>(0);
const showFilters = ref<boolean>(false);
const productFilters = ref<FilterMenuItem[]>([]);
const searchInput = ref<string>("");
const sortAscending = ref<boolean>(true);

const viewLoaded = ref(false);
const showDownloadsModal = ref(false);
const showProductsCartModal = ref(false);
const productCartType = ref<SubApplicationType>(SubApplicationType.Copy);
const viewNew = ref(false);
const gpcPlantId = ref<string>("");
const gpcPlantIdLoaded = ref(false);

const plants = ref<IdAndName[]>([]);

const copyModalVisible = ref<boolean>(false);
const terminationModalVisible = ref<boolean>(false);

const statusList = [
  EnumMerchStatus[EnumMerchStatus.Certified],
  EnumMerchStatus[EnumMerchStatus.Pending],
  EnumMerchStatus[EnumMerchStatus.Terminated],
  EnumMerchStatus[EnumMerchStatus.Rejected],
  EnumMerchStatus[EnumMerchStatus.Withdrawn],
  EnumMerchStatus[EnumMerchStatus.WebApp],
];

const cartActions = ref([
  {
    name: "Complete order: Add Products",
    count: scheduleStore.getCartCount(SubApplicationType.Add),
    subApplication: SubApplicationType.Add,
    class: "add-products",
    icon: "add",
    onClick: () => {
      productCartType.value = SubApplicationType.Add;
      showProductsCartModal.value = true;
    },
  },
  {
    name: "Complete order: Copy Products",
    count: scheduleStore.getCartCount(SubApplicationType.Copy),
    subApplication: SubApplicationType.Copy,
    class: "copy-products",
    icon: "add",
    onClick: () => {
      productCartType.value = SubApplicationType.Copy;
      showProductsCartModal.value = true;
    },
  },
  {
    name: "Complete order: Delete Products",
    count: scheduleStore.getCartCount(SubApplicationType.Terminate),
    subApplication: SubApplicationType.Terminate,
    class: "terminate-products",
    icon: "delete",
    onClick: () => {
      productCartType.value = SubApplicationType.Terminate;
      showProductsCartModal.value = true;
    },
  },
]);

const canOpenOrModifyCarts = computed(() => {
  return (
    (main.companySummary &&
      main.companySummary.denials.every(
        (d) => !d.Products || d.Products.toUpperCase() !== "ALL"
      ) &&
      main.companySummary.status !== EnumCompanyStatus.Pending &&
      gpcPlantIdLoaded.value &&
      !gpcPlantId.value &&
      ProductAppGroup.roles.some((r) => main.isInRole(r))) ??
    false
  );
});

const selectedProductIds = computed(() => {
  return products.value
    .filter((product) => product.selected)
    .map((product) => product.ProducedInOneId);
});

const valueFiltersConvertOptions = [
  {
    column: "_Dpm",
    convert: (value: number) => {
      return EnumDpm[value];
    },
  },
  {
    column: "_Group",
    convert: (value: number) => {
      return convertNumber(value);
    },
  },
  {
    column: "_LabelType",
    convert: (value: number) => {
      return getLabelTypeString(value as EnumLabelType);
    },
  },
  {
    column: "_Status",
    convert: (value: number) => {
      return EnumMerchStatus[value];
    },
  },
  {
    column: "_Pesach",
    convert: boolYNConvert,
  },
] as ValueFieldConvertOption[];

const sortOptions = [
  {
    text: "Sort By",
    value: "",
  },
  {
    text: "Label Name & Num",
    value: "LabelName",
  },
  {
    text: "Brand",
    value: "BrandName",
  },
  {
    text: "Plant",
    value: "PlantName",
  },
];

const selectedSortOption = ref<{ text: string; value: string }>(sortOptions[0]);

const getProductDisplayScheduleBWithFiltering = async function (
  valuesForFieldOnly: string = ""
) {
  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  if (!valuesForFieldOnly) {
    productFilters.value.forEach((f) => {
      const filterGroup = {
        FilterFields: [],
        BooleanLogicOperator: EnumFilterStatementConnector.And,
        FieldName: f.field,
        InternalFieldName: "",
      } as FilterGroup;

      if (f.value === "Both") return;

      const filterField = {
        Operator: f.getOpValue ? f.getOpValue(f.value) : "eq",
        Value: f.getFilterValue ? f.getFilterValue(f.value) : f.value,
      } as FilterField;

      filterGroup.FilterFields.push(filterField);
      filterModel.FilterGroups.push(filterGroup);
    });
  }

  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchInput.value,
    plantList: plants.value.map((p) => p.ID).join(";"),
    statusList: statusList.join(";"),
    allPlantCompanies: false,
    includeConfidential: true,
    filters: JSON.stringify(filterModel),
    take: productsTake.value,
    skip: productsSkip.value,
    sortField: selectedSortOption.value.value,
    ascending: sortAscending.value,
  } as GetProductDisplayScheduleBWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetProductDisplayScheduleBWithFiltering", requestParams);

  requestParams.valuesForFieldOnly = valuesForFieldOnly;
  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(
    createCacheKey(requestParams, "GetProductDisplayScheduleBWithFiltering"),
    () => api.get("GetProductDisplayScheduleBWithFiltering", requestParams)
  );
};

const getProductsAsync = async function () {
  if (productsLoading.value) return;
  productsLoading.value = true;

  await handleResponsePromiseAsync(
    getProductDisplayScheduleBWithFiltering(),
    (data) => {
      products.value = data.ListItems;
      totalProducts.value = data.TotalItemsInList;
    },
    "ScheduleBMobileView.vue",
    "getProductsAsync"
  );

  productsLoading.value = false;
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  productsSkip.value = 0;
  searchMatchingProductsAsync();
};

const onProductsChanged = function (skip: number, take: number) {
  productsTake.value = take;
  productsSkip.value = skip;
  searchMatchingProductsAsync(skip, true);
};

const onFiltersApplied = function (filters: FilterMenuItem[]) {
  productFilters.value = filters;
  productsSkip.value = 0;
  searchMatchingProductsAsync();
};

const onSortChanged = function () {
  productsSkip.value = 0;
  searchMatchingProductsAsync();
};

const onFiltersCleared = function () {
  productFilters.value = [];
  productsSkip.value = 0;
  searchMatchingProductsAsync();
};

const onCopyClicked = function () {
  (plants.value as PlantWithSelection[]).forEach((p) => (p.selected = false));
  copyModalVisible.value = true;
};

const onTerminateClicked = function () {
  terminationModalVisible.value = true;
};

const onCopyModalClosed = function (cartCount: number) {
  copyModalVisible.value = false;

  if (cartCount > -1) {
    clearSelection();
  }
};

const onTerminationModalClosed = function (cartCount: number) {
  terminationModalVisible.value = false;

  if (cartCount > -1) {
    clearSelection();
  }
};

const clearSelection = function () {
  products.value.forEach((p) => (p.selected = false));
};

const onCartCountUpdate = function (type: SubApplicationType, count: number) {
  const action = cartActions.value.find(
    (action) => action.subApplication === type
  );
  if (action) {
    action.count = count;
  }
};

const searchMatchingProductsAsync = async function (
  skip: number = 0,
  appendProducts: boolean = false
) {
  if (productsLoading.value) return;
  productsLoading.value = true;
  await handleResponsePromiseAsync(
    getProductDisplayScheduleBWithFiltering(""),
    (data) => {
      if (!appendProducts) products.value = data.ListItems;
      else products.value = [...products.value, ...data.ListItems];
      totalProducts.value = data.TotalItemsInList;
    },
    "ScheduleBMobileView.vue",
    "SearchMatchingProducts"
  );
  productsLoading.value = false;
};

const getPropValuesAsync = async function (field: string) {
  let items: ProductWithSelection[] = [];

  await handleResponsePromiseAsync(
    getProductDisplayScheduleBWithFiltering(field),
    (data) => {
      console.debug(
        `ScheduleBMobileView.vue: getPropValuesAsync(), result:`,
        data
      );
      items = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, field!, items);
    },
    "ScheduleBMobileView.vue",
    "getPropValuesAsync"
  );
  return items;
};

const getPlantsAsync = async function () {
  return api.get("GetAuthorizedCertifiedOrPendingOwns", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    certifiedOnly: true,
  });
};

const updatePlantListAsync = async function () {
  return handleResponsePromiseAsync(
    getPlantsAsync(),
    (data) => {
      plants.value = data;
    },
    "ScheduleBMobileView",
    "updatePlantListAsync"
  );
};

const updateProductsInCartCountAsync = async function () {
  const promises = cartActions.value.map(async (action) => {
    return handleResponsePromiseAsync(
      getProductsInCartCountAsync(action.subApplication),
      (data) => {
        scheduleStore.setCartCount(action.subApplication, data);
      },
      "ScheduleBMobileView",
      "updateProductsInCartCountAsync"
    );
  });
  await Promise.all(promises);
};

const getProductsInCartCountAsync = async function (subApplication: string) {
  return api.get(
    "VueGetAllMatchingProductsInCartSubApplicationAndUserLoginCount",
    {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: subApplication,
    }
  );
};

const updateGpcPlantIdAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetGpcPlantId", {
      userLogin: main.userLogin,
    }),
    (data) => {
      gpcPlantId.value = data;
      gpcPlantIdLoaded.value = true;
    },
    "ScheduleBMobileView",
    "updateGpcPlantId"
  );
};

scheduleStore.handleCartCountUpdate = onCartCountUpdate;

onBeforeMount(async () => {
  updateProductsInCartCountAsync();
  await updatePlantListAsync();
  getProductsAsync();
  updateGpcPlantIdAsync();
});
</script>

<style scoped lang="scss">
.search-section {
  background: #222;
  border-top: 1px solid #333;
  display: flex;
  align-items: center;
  padding: 10px 15px;

  .sched-a-search {
    flex: 1;
    overflow: hidden;
  }

  .search-wrapper {
    position: relative;
    display: flex;
    flex: 1;

    .product-mobile-search {
      width: 100%;
    }
  }

  .filter-icon {
    cursor: pointer;
    position: absolute;
    right: 0px;
    height: 100%;
    top: 0px;
    background: lightgrey;
    padding: 4px 7px 0;
  }
}

.items-section {
  padding: 20px 15px;
}
.items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sort-wrapper {
  display: flex;
  align-items: center;
  padding: 10px;
  // margin-top: 24px;
  justify-content: center;
  width: 40%;

  .sort-dropdown {
    width: 100%;
    max-width: 340px;
  }
}

.items-section {
  padding: 20px 15px;
}
.items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

//actions
.action-island {
  margin-left: auto;
  display: flex;
  gap: 10px;
  &__carts {
    display: flex;
    flex: 1 1 70%;
    gap: 10px;
  }

  .download,
  button {
    background: #444;
    color: white;
    cursor: pointer;
    border-radius: 4px;
    padding: 5px 15px;
    flex: 1;
    display: flex;
    gap: 5px;
    font-size: 12px;
    align-items: center;
  }
  .add-products {
    margin-top: 10px;
    margin-bottom: 10px;
    background: #198754; // blue;
  }
  .copy-products {
    margin-top: 10px;
    margin-bottom: 10px;
    background: #428bca; // #008a00;
  }
  .terminate-products {
    margin-top: 10px;
    margin-bottom: 10px;
    background: #d9534f; // red;
  }
  .download {
    margin-top: 10px;
    margin-bottom: 10px;
    flex: 1 1 10%;
  }
}

.button-island {
  margin-left: auto;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  > * {
    color: white;
    cursor: pointer;
    border-radius: 4px;
    padding: 5px 15px;
    flex: 1;
    display: flex;
    gap: 5px;
    font-size: 12px;
    align-items: center;
    margin-top: 10px;
    &.disabled {
      opacity: 0.4;
    }

    &.add-products {
      margin-top: 10px;
      margin-bottom: 10px;
      background: #198754; // blue;
    }
    &.copy-products {
      margin-top: 10px;
      margin-bottom: 10px;
    }
    &.terminate-products {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }
}

//new-product
.new-product {
  .trigger {
    position: fixed;
    bottom: 20px;
    right: 10px;
    background: #428bca;
    color: white;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 200;
    cursor: pointer;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
    z-index: 1;
    &:active {
      background: #204f77;
    }
  }
  .new-product-screen {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 99999;
    background: white;
    > header {
      background: #222;
      color: white;
      font-weight: bold;
      padding: 10px 15px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    > nav {
      border-bottom: 1px solid #ddd;
      padding: 0px 15px 0;
      display: flex;
      gap: 0;
      background: #efefef;
    }
    > nav > span {
      cursor: pointer;
      display: inline-block;
      padding: 10px 15px;
      margin-bottom: -1px;
      border-bottom: 2px solid transparent;
      &.active {
        border-color: #428bca;
        color: #428bca;
      }
    }
    > main {
      padding: 10px 25px;
    }
    button {
      border: none;
      border-radius: 6px;
      padding: 8px 12px;
      background: #428bca;
      color: white;
      flex: 3;
      border: 1px solid transparent;
    }
  }
}
</style>
